import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FolderOpen, Receipt, FileText, ClipboardCheck } from "lucide-react";
import { useCustomerDashboard } from "@/hooks/useCustomerPortal";
import { useTabNavigate } from "@/hooks/useTabNavigate";

const CustomerDashboard = () => {
  const navigate = useTabNavigate();
  const {
    activeProjects, totalProjects, openInvoiceCount, openInvoiceTotal,
    pendingCOs, pendingApprovals, isLoading, recentInvoices, recentProjects,
  } = useCustomerDashboard();

  const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  const tiles = [
    { label: "Active Projects", value: activeProjects, sub: `${totalProjects} total`, icon: FolderOpen, path: "/portal/customer/projects" },
    { label: "Open Invoices", value: openInvoiceCount, sub: fmt(openInvoiceTotal), icon: Receipt, path: "/portal/customer/invoices" },
    { label: "Pending Change Orders", value: pendingCOs, sub: "awaiting review", icon: FileText, path: "/portal/customer/change-orders" },
    { label: "Pending Approvals", value: pendingApprovals, sub: "items to review", icon: ClipboardCheck, path: "/portal/customer/approvals" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground text-sm">Overview of your projects and financials</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate(t.path)}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t.label}</CardTitle>
              <t.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{t.value}</div>
              <p className="text-xs text-muted-foreground">{t.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Projects</CardTitle></CardHeader>
          <CardContent>
            {recentProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No projects yet</p>
            ) : (
              <div className="space-y-3">
                {recentProjects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-md px-2 py-1.5 -mx-2" onClick={() => navigate(`/portal/customer/projects/${p.id}`)}>
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.project_number}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Recent Invoices</CardTitle></CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">No invoices yet</p>
            ) : (
              <div className="space-y-3">
                {recentInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{inv.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">{inv.invoice_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{fmt(inv.total)}</p>
                      <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDashboard;
